import { YES_NO, applicantSection, otherAttachments, proxyAttachments, replySection, representationSection } from "modules/ebiForms/common";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * E4. Zgoda wynajmującego (gminy / jednostki zarządzającej zasobem) na podnajem lub oddanie lokalu do bezpłatnego
 * używania — art. 688² Kodeksu cywilnego, albo na remont i ulepszenia lokalu — art. 6d ustawy z 21.06.2001 r. o ochronie
 * praw lokatorów (ulepszenia tylko za zgodą i na podstawie pisemnej umowy o rozliczeniu). Czynność wynajmującego, nie
 * organu administracji — bez decyzji. Dwa warianty (wariant wniosku w BE). Brak wzoru ogólnopolskiego; układ: podnajem —
 * wzór ZGM Gliwice (https://zgm-gliwice.pl/wp-content/uploads/2026/10/Wniosek_o_podnajem.pdf, pobrany 22.10.2026: wykaz
 * osób, uzasadnienie, załączniki; część „wypełnia zarządca” pominięta), remont — wzór MZZL Sosnowiec (zał. 1 do
 * zarządzenia 51/2021, https://www.mzzl.pl/uploads/files/wniosek.pdf, pobrany 22.10.2026: lista robót do zaznaczenia,
 * szkic zmian). Wzoru ZKZL Poznań nie pobrano (ochrona antybotowa serwisu). Ograniczenia podnajmu w uchwale rady gminy
 * — do potwierdzenia z wydziałem.
 */
const SUBLET: EbiCondition = { field: "rodzajZgody", equals: "podnajem" };
const RENOVATION: EbiCondition = { field: "rodzajZgody", equals: "remont" };
const PART_OF_FLAT: EbiCondition = { all: [SUBLET, { field: "zakresPodnajmu", equals: "czesc" }] };
const works = (value: string): EbiCondition => ({ all: [RENOVATION, { field: "zakresRobot", includes: value }] });
const LAYOUT_CHANGE: EbiCondition = {
  all: [
    RENOVATION,
    {
      any: ["scianki", "wodKanNowa", "elektrycznaNowa", "gazowaNowa"].map((value): EbiCondition => ({ field: "zakresRobot", includes: value })),
    },
  ],
};

const E4: IEbiFormDefinition = {
  code: "E4",
  version: 1,
  title: "Wniosek o zgodę na podnajem lokalu albo na jego remont lub ulepszenie",
  category: EEbiFormCategory.Housing,
  office: EEbiFormOffice.MunicipalUnit,
  description: "Zgoda wynajmującego na podnajem (bezpłatne używanie) lokalu komunalnego albo na wykonanie w nim remontu lub ulepszeń.",
  keywords: ["podnajem", "użyczenie", "remont mieszkania", "ulepszenia", "zgoda wynajmującego", "mieszkanie komunalne"],
  organ: "Wynajmujący — gmina albo jednostka zarządzająca jej mieszkaniowym zasobem (np. ZKZL, ZGM, MZZL)",
  legalBasis:
    "art. 688² ustawy z dnia 23 kwietnia 1964 r. — Kodeks cywilny (podnajem i bezpłatne używanie lokalu); art. 6d i 6e ustawy z dnia 21 czerwca 2001 r. o ochronie praw lokatorów, mieszkaniowym zasobie gminy i o zmianie Kodeksu cywilnego (ulepszenia); uchwała rady gminy w sprawie zasad wynajmowania lokali",
  request: "Wnoszę o wyrażenie zgody na czynność wskazaną we wniosku, dotyczącą zajmowanego przeze mnie lokalu.",
  feeLabel: "bez opłaty",
  fee: "Bez opłaty. Pełnomocnictwo — 17 zł opłaty skarbowej.",
  variantField: "rodzajZgody",
  variants: {
    podnajem: { title: "Wniosek o wyrażenie zgody na podnajem lub oddanie lokalu do bezpłatnego używania" },
    remont: { title: "Wniosek o wyrażenie zgody na remont lub ulepszenie lokalu" },
  },
  sections: [
    applicantSection("Wnioskodawca (najemca lokalu)"),
    representationSection(),
    {
      code: "lokal",
      title: "Przedmiot wniosku",
      fields: [
        {
          code: "rodzajZgody",
          type: "radio",
          label: "Wnoszę o wyrażenie zgody na",
          required: true,
          options: [
            { value: "podnajem", label: "podnajem lokalu albo oddanie go do bezpłatnego używania (art. 688² Kodeksu cywilnego)" },
            { value: "remont", label: "wykonanie remontu lub ulepszeń w lokalu (art. 6d ustawy o ochronie praw lokatorów)" },
          ],
        },
        { code: "adresLokalu", type: "text", label: "Adres lokalu — ulica, numer budynku i lokalu, miejscowość", required: true, maxLength: 300 },
      ],
    },
    {
      code: "podnajem",
      title: "Podnajem lub bezpłatne używanie",
      visibleWhen: SUBLET,
      fields: [
        {
          code: "formaUzywania",
          type: "radio",
          label: "Forma",
          required: true,
          options: [
            { value: "podnajem", label: "podnajem (odpłatnie)" },
            { value: "uzywanie", label: "oddanie do bezpłatnego używania" },
          ],
        },
        {
          code: "zakresPodnajmu",
          type: "radio",
          label: "Zakres",
          required: true,
          options: [
            { value: "calosc", label: "cały lokal" },
            { value: "czesc", label: "część lokalu" },
          ],
        },
        { code: "czescLokalu", type: "text", label: "Która część lokalu", required: true, visibleWhen: PART_OF_FLAT, maxLength: 200, placeholder: "np. jeden pokój" },
        { code: "okresOd", type: "date", label: "Od dnia", required: true, width: "half" },
        { code: "okresDo", type: "date", label: "Do dnia", required: true, width: "half" },
        {
          code: "podnajemcy",
          type: "repeat",
          label: "Osoby, którym lokal ma zostać podnajęty lub oddany do używania",
          itemLabel: "Osoba",
          required: true,
          min: 1,
          max: 10,
          fields: [
            { code: "imieNazwisko", type: "text", label: "Imię i nazwisko", required: true, width: "half", maxLength: 200 },
            { code: "pesel", type: "pesel", label: "PESEL", required: true, width: "half" },
            { code: "pokrewienstwo", type: "text", label: "Stopień pokrewieństwa z najemcą (jeśli jest)", width: "half", maxLength: 100 },
            { code: "innyTytul", type: "select", label: "Czy ma tytuł prawny do innego lokalu?", required: true, width: "half", options: YES_NO },
          ],
        },
        {
          code: "uzasadnieniePodnajmu",
          type: "textarea",
          label: "Uzasadnienie wniosku",
          hint: "Np. przyczyna i okres czasowej nieobecności w lokalu.",
          required: true,
          maxLength: 5000,
        },
      ],
    },
    {
      code: "remont",
      title: "Remont lub ulepszenia",
      visibleWhen: RENOVATION,
      fields: [
        {
          code: "zakresRobot",
          type: "checkboxes",
          label: "Zakres robót",
          required: true,
          options: [
            { value: "wylewki", label: "wymiana istniejących wylewek" },
            { value: "podlogi", label: "wymiana podłóg, parkietów, paneli, płytek itp." },
            { value: "tynki", label: "wykonanie nowych tynków, gładzi gipsowych" },
            { value: "okna", label: "wymiana stolarki okiennej" },
            { value: "drzwi", label: "wymiana stolarki drzwiowej" },
            { value: "malowanie", label: "malowanie, tapetowanie lub inne okładziny ścienne i sufitowe" },
            { value: "zabudowa", label: "zabudowa dekoracyjna (np. płytami g-k, OSB)" },
            { value: "klimatyzator", label: "montaż klimatyzatora" },
            { value: "plytki", label: "płytki ceramiczne podłogowe lub ścienne (albo inne okładziny) w łazience, WC, kuchni" },
            { value: "wodKanIstniejaca", label: "instalacja wod.-kan. — wymiana po trasie istniejącej" },
            { value: "wodKanNowa", label: "instalacja wod.-kan. — wymiana po nowej trasie" },
            { value: "elektrycznaIstniejaca", label: "instalacja elektryczna — wymiana po trasie istniejącej" },
            { value: "elektrycznaNowa", label: "instalacja elektryczna — wymiana po nowej trasie" },
            { value: "odcinekLicznika", label: "wymiana odcinka od licznika do zabezpieczeń w lokalu" },
            { value: "gazowaIstniejaca", label: "instalacja gazowa — wymiana po trasie istniejącej" },
            { value: "gazowaNowa", label: "instalacja gazowa — wymiana po nowej trasie" },
            { value: "likwidacjaGazu", label: "likwidacja instalacji gazowej" },
            { value: "kuchenka", label: "wymiana kuchenki gazowej na elektryczną" },
            { value: "scianki", label: "likwidacja lub wykonanie nowych ścianek działowych" },
            { value: "grzejniki", label: "wymiana grzejników" },
            { value: "inne", label: "inne" },
          ],
        },
        { code: "liczbaOkien", type: "number", label: "Liczba okien do wymiany [szt.]", required: true, visibleWhen: works("okna"), width: "half", min: 1, max: 50 },
        { code: "liczbaDrzwi", type: "number", label: "Liczba drzwi do wymiany [szt.]", required: true, visibleWhen: works("drzwi"), width: "half", min: 1, max: 50 },
        {
          code: "grzejnikiPomieszczenia",
          type: "text",
          label: "Pomieszczenia, w których zostaną wymienione grzejniki",
          required: true,
          visibleWhen: works("grzejniki"),
          maxLength: 300,
        },
        { code: "inneRoboty", type: "text", label: "Inne roboty — jakie", required: true, visibleWhen: works("inne"), maxLength: 500 },
        { code: "uwagiRemont", type: "textarea", label: "Inne uwagi", hint: "Np. planowany termin i sposób wykonania robót.", maxLength: 3000 },
        {
          code: "infoUlepszenia",
          type: "info",
          label:
            "Ulepszenia lokalu najemca może wykonać tylko za zgodą wynajmującego i na podstawie pisemnej umowy określającej sposób rozliczeń (art. 6d ustawy o ochronie praw lokatorów). Jeśli roboty wymagają zgłoszenia albo pozwolenia na budowę, zgoda właściciela jest załącznikiem do zgłoszenia lub wniosku w starostwie.",
        },
      ],
    },
    replySection(),
  ],
  attachments: [
    {
      code: "oswiadczeniePodnajemcy",
      label: "Wniosek albo oświadczenie podpisane przez osoby ubiegające się o podnajem (używanie) lokalu",
      hint: "Skan dokumentu z podpisem każdej z tych osób.",
      visibleWhen: SUBLET,
      required: true,
      multiple: true,
    },
    {
      code: "nieobecnosc",
      label: "Dokument uzasadniający czasową nieobecność najemcy (np. umowa o pracę poza miejscem zamieszkania, zaświadczenie lekarskie)",
      hint: "Nieobowiązkowo — część zarządców o niego prosi.",
      visibleWhen: SUBLET,
    },
    {
      code: "potwierdzenieZarzadcy",
      label: "Potwierdzenie zarządcy o stanie zaległości w opłatach za lokal",
      hint: "Nieobowiązkowo — część wniosku zwykle wypełnia administracja budynku.",
      visibleWhen: SUBLET,
    },
    {
      code: "szkic",
      label: "Szkic lokalu z naniesionymi zmianami",
      hint: "Rysunek lub skan (PDF, JPG) — przy zmianie ścianek działowych albo przebiegu instalacji.",
      visibleWhen: LAYOUT_CHANGE,
      required: true,
    },
    {
      code: "projekt",
      label: "Projekt lub opinia osoby z uprawnieniami budowlanymi",
      hint: "Nieobowiązkowo — zarządcy często go wymagają przy zmianie ścianek lub instalacji.",
      visibleWhen: LAYOUT_CHANGE,
    },
    ...proxyAttachments(),
    otherAttachments(),
  ],
};

export default E4;
